<template>
  <div class="profile-data">
    <div class="ui-wrapper">
      <div class="profile-data__avatar">
        <UiAvatar
          :user="profile"
          size="xl"
          increasing
        />

        <div
          v-if="profile.isTrusted"
          class="profile-data__trust"
        >
          <UIIcon name="trust-user-mark" />
        </div>
      </div>

      <div class="profile-data__info">
        <div class="profile-data__info-top">
          <div class="profile-data__title playfair">
            {{ profile.nickname }}
          </div>

          <template v-if="profile.isMyProfile">
            <LazyAccountAvatarLoader v-if="showImageLoad" />

            <UIButton
              class="profile-data__info-button profile-data__info-button_mobile"
              type="black"
              size="xs"
              href="/account"
            >
              {{ $t('components.profile.profileData.backToAccount') }}
            </UIButton>
          </template>

          <UIButton
            v-else-if="!profile.isMyProfile"
            class="profile-data__info-button"
            :type="isFollowed ? 'outline' : 'black'"
            size="xs"
            :loading="followLoading"
            @click="follow"
          >
            {{ followedText }}
          </UIButton>
        </div>

        <div
          v-if="profile.communityBadge"
          class="flex profile-data__badge mt-2"
        >
          <account-o-community-badge
            :model-value="profile.communityBadge"
            :user="profile"
            :owner-mode="profile.isMyProfile"
            with-dialog
            @mounted="onBadgeMount"
            @click="onBadgeClick"
          />
        </div>

        <div class="profile-data__text-info">
          <p
            v-if="profile.registrationTime"
            class="profile-data__text"
          >
            {{ $t('components.profile.profileData.registrationTime', { registrationDate: registrationDateStr }) }}
          </p>

          <div class="profile-data__text-type">
            <template v-if="profile.isPro">
              <span class="profile-data__text">{{ $t('components.profile.profileData.isPro.text') }}</span>

              <UIHint center>
                {{ $t('components.profile.profileData.isPro.hint') }}
              </UIHint>
            </template>

            <template v-else>
              <span class="profile-data__text">{{ $t('components.profile.profileData.privateSeller.text') }}</span>

              <UIHint>
                {{ $t('components.profile.profileData.privateSeller.hint') }}
              </UIHint>
            </template>
          </div>

          <div
            v-if="!profile.isPro && profile.acceptsReturns"
            class="profile-data__text-type"
          >
            <span class="profile-data__text">{{ $t('components.profile.profileData.acceptsReturns.text') }}</span>

            <UIHint>
              {{ $t('components.profile.profileData.acceptsReturns.hint') }}
            </UIHint>
          </div>
        </div>

        <ul class="profile-data__bottom">
          <li class="profile-data__bottom-item">
            <nuxt-link :to="`/profile/${profile.id}`">
              <div class="profile-data__count-number">
                {{ profile.productsCount }}
              </div>

              <div class="profile-data__count-label">
                {{ $t('components.profile.profileData.productsCount', profile.productsCount) }}
              </div>
            </nuxt-link>
          </li>

          <li
            class="profile-data__bottom-item profile-data__bottom-item_clickable"
            @click="showModal('followers')"
          >
            <div class="profile-data__count-number">
              {{ profile.followersCount }}
            </div>

            <div class="profile-data__count-label">
              {{ $t('components.profile.profileData.followersCount', profile.followersCount) }}
            </div>
          </li>

          <li
            class="profile-data__bottom-item profile-data__bottom-item_clickable"
            @click="showModal('followings')"
          >
            <div class="profile-data__count-number">
              {{ profile.followingsCount }}
            </div>

            <div class="profile-data__count-label">
              {{ $t('components.profile.profileData.followingsCount', profile.followingsCount) }}
            </div>
          </li>
        </ul>
      </div>
    </div>

    <BusinessUsersModal
      v-model="isShowModal"
      :user-id="profile.id"
      :user-type="usersType"
      @change-follow="handleFollow"
    />
  </div>
</template>

<script lang="ts" setup>
import { AnalyticsEvents } from '~/types/analytics';
import { PublicProfileDTO } from '~/restAPI/data-contracts';

type TUserType = 'followers' | 'followings';

const props = defineProps<{
  profile: Required<PublicProfileDTO>
  showImageLoad?: boolean
}>();
const emit = defineEmits<{
  (e: 'updateUser'): void,
}>();

const { isAuth, openAuthDialog } = useAccountStore();

const {
  followLoading,
  isFollowed,
  follow,
  followedText,
} = useFollowUser(props.profile);
const { $addEvent } = useNuxtApp();

const isShowModal = ref(false);
const usersType = ref<TUserType>('followers');

const registrationDateStr = computed(() => {
  if (!props.profile.registrationTime) return '';

  const months = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ];
  const registerDate = new Date(props.profile.registrationTime! * 1000);

  return `${months[registerDate.getMonth()]} ${registerDate.getFullYear()}`;
});

function showModal(name: TUserType) {
  if (!isAuth.value) {
    openAuthDialog({ chapter: 'subscribe_user' });
    return;
  }

  usersType.value = name;
  isShowModal.value = true;
}
function handleFollow() {
  if (props.profile.isMyProfile) {
    emit('updateUser');
  }
}
function onBadgeMount() {
  if (!props.profile.isMyProfile) return;

  $addEvent(AnalyticsEvents.OCommunityBadgeView, {
    page: 'public_profile_my',
    id_chapter: String(props.profile.id),
  });
}
function onBadgeClick() {
  if (!props.profile.isMyProfile) return;

  $addEvent(AnalyticsEvents.OCommunityBadgeClick, {
    page: 'public_profile_my',
    id_item: props.profile.communityBadge?.status?.code,
    id_chapter: String(props.profile.id),
  });
}
</script>

<style scoped lang="scss">
.profile-data {
  text-align: center;
  padding: 12px 0 0;
  margin-bottom: 12px;

  @media screen and (min-width: 767px) {
    padding: 54px 0 14px;
    text-align: left;
  }

  .ui-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;

    @media screen and (min-width: 767px) {
      flex-direction: row;
      justify-content: center;
      flex-wrap: wrap;
    }
  }

  :deep(.user-avatar) {
    margin: 0;
  }

  &__avatar {
    position: relative;
    margin-bottom: 8px;

    @media screen and (min-width: 767px) {
      margin-bottom: 0;

      :deep(.user-avatar) {
        width: 173px;
        height: 173px;
      }
    }
  }

  &__trust {
    background: #fff;
    border-radius: 100%;
    width: 18px;
    height: 18px;
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3px;

    @media screen and (min-width: 767px) {
      width: 35px;
      height: 35px;
      top: 6px;
      right: 6px;
    }
  }

  &__info {
    position: relative;
    padding-bottom: 60px;
    width: 100%;

    @media screen and (min-width: 767px) {
      width: auto;
      padding: 0;
      margin-left: 60px;
    }
  }

  &__info-top {
    @media screen and (min-width: 767px) {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
  }

  &__badge {
    @media screen and (max-width: 766px) {
      justify-content: center;
      margin-bottom: 8px;
    }
  }

  &__info-button {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;

    @media screen and (min-width: 767px) {
      position: static;
      width: 147px;
      margin-left: 8px;
      transform: translateY(1px);
    }

    &_mobile {
      @media screen and (min-width: 767px) {
        display: none;
      }
    }
  }

  &__title {
    font-size: 24px;
    line-height: 27px;
    text-align: center;
    letter-spacing: 0;
    margin-bottom: 12px;

    @media screen and (min-width: 767px) {
      margin-bottom: 0;
      margin-right: 16px;
      text-align: left;
      font-size: 44px;
      line-height: 48px;
      letter-spacing: 1.4px;
    }
  }

  &__text-info {
    font-size: 14px;
    line-height: 17px;
    color: #bdbdbd;

    @media screen and (min-width: 767px) {
      display: flex;
      align-items: center;
      margin-top: 24px;
    }
  }

  &__text {
    @media screen and (min-width: 767px) {
      font-size: 16px;
      line-height: 19px;
    }
  }

  &__text-type {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;

    :deep(.ui-hint) {
      margin-left: 4px;
    }

    @media screen and (min-width: 767px) {
      margin-top: 0;

      &:before {
        content: '';
        width: 5px;
        height: 5px;
        background: #333;
        opacity: .2;
        margin: 0 8px;
      }
    }
  }

  &__bottom {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 20px;

    @media screen and (min-width: 767px) {
      justify-content: start;
    }
  }

  &__bottom-item {
    margin: 0 10px;

    @media screen and (min-width: 767px) {
      margin: 0;
      margin-right: 32px;
    }

    &_clickable {
      cursor: pointer;
    }
  }

  &__count-number {
    font-weight: 800;
    line-height: 19px;

    @media screen and (min-width: 767px) {
      font-size: 19px;
      line-height: 22px;
    }
  }

  &__count-label {
    font-size: 12px;
    line-height: 16px;
    margin-top: 4px;
    color: #828282;

    @media screen and (min-width: 767px) {
      font-size: 14px;
      line-height: 17px;
    }
  }
}
</style>
